import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getValidToken, API_URL } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS, clearPermissionsCache } from './utils/adminPermissions';
import { MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminCuratorManagement = () => {
  const navigate = useNavigate();
  const [curators, setCurators] = useState([]);
  const [availableAlumni, setAvailableAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [permissions, setPermissions] = useState({
    can_post_to_network: false,
    can_access_news_curation: false
  });
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchCurators();
  }, []);

  const fetchCurators = async () => {
    try {
      const token = getValidToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/admin/curators`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setCurators(response.data.curators || []);
    } catch (err) {
      console.error('Error fetching curators:', err);
      setError('Failed to load curators');
    } finally {
      setLoading(false);
    }
  };

  const searchAvailableAlumni = async (query) => {
    if (!query || query.length < 2) {
      setAvailableAlumni([]);
      return;
    }

    try {
      const token = getValidToken();
      const response = await axios.get(`${BACKEND_URL}/api/admin/curators/available`, {
        params: { search: query },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setAvailableAlumni(response.data.alumni || []);
    } catch (err) {
      console.error('Error searching alumni:', err);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchAvailableAlumni(query);
  };

  const openGrantModal = (alumni) => {
    setSelectedAlumni(alumni);
    setPermissions({
      can_post_to_network: false,
      can_access_news_curation: false
    });
    setShowGrantModal(true);
  };

  const closeGrantModal = () => {
    setShowGrantModal(false);
    setSelectedAlumni(null);
    setSearchQuery('');
    setAvailableAlumni([]);
    setPermissions({
      can_post_to_network: false,
      can_access_news_curation: false
    });
  };

  const handleGrantAccess = async () => {
    if (!selectedAlumni) return;

    if (!permissions.can_post_to_network && !permissions.can_access_news_curation) {
      setError('Please select at least one permission');
      return;
    }

    try {
      setProcessingId(selectedAlumni.id);
      setError('');
      const token = getValidToken();

      await axios.put(
        `${BACKEND_URL}/api/admin/curators/${selectedAlumni.id}`,
        permissions,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      await fetchCurators();
      closeGrantModal();
      alert('Curator access granted successfully!');
    } catch (err) {
      console.error('Error granting access:', err);
      setError(err.response?.data?.detail || 'Failed to grant curator access');
    } finally {
      setProcessingId(null);
    }
  };

  const handleUpdatePermissions = async (curatorId, currentPermissions) => {
    try {
      setProcessingId(curatorId);
      setError('');
      const token = getValidToken();

      await axios.put(
        `${BACKEND_URL}/api/admin/curators/${curatorId}`,
        currentPermissions,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      await fetchCurators();
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError(err.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRevokeAccess = async (curatorId, curatorName) => {
    if (!window.confirm(`Revoke all curator access for ${curatorName}?`)) return;

    try {
      setProcessingId(curatorId);
      setError('');
      const token = getValidToken();

      await axios.delete(`${BACKEND_URL}/api/admin/curators/${curatorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      await fetchCurators();
      alert('Curator access revoked successfully!');
    } catch (err) {
      console.error('Error revoking access:', err);
      setError(err.response?.data?.detail || 'Failed to revoke access');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.CONTENT_CURATION}>
      {/* Mobile Sidebar */}
      <MobileSidebar
        activeTab="curator-management"
        onTabChange={(tabId) => {
          if (tabId === 'home') {
            navigate('/admin');
          } else if (tabId === 'profile') {
            localStorage.removeItem('token');
            localStorage.removeItem('authToken');
            clearPermissionsCache();
            navigate('/');
          }
        }}
        navigate={navigate}
        isOpen={isMobileSidebarOpen}
        onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Curator Access Management</h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
                Grant alumni users access to content curation features
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
              <button
                onClick={() => setShowGrantModal(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Grant Access
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Back to Admin
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('authToken');
                  clearPermissionsCache();
                  navigate('/');
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Current Curators */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Current Curators ({curators.length})</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alumni User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permissions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Posts Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {curators.map((curator) => (
                  <tr key={curator.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {curator.first_name} {curator.last_name}
                        </div>
                        <div className="text-sm text-gray-500">{curator.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={curator.can_access_news_curation}
                            onChange={(e) => handleUpdatePermissions(curator.id, {
                              can_post_to_network: curator.can_post_to_network,
                              can_access_news_curation: e.target.checked
                            })}
                            disabled={processingId === curator.id}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span>Can access curation</span>
                        </label>
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={curator.can_post_to_network}
                            onChange={(e) => handleUpdatePermissions(curator.id, {
                              can_post_to_network: e.target.checked,
                              can_access_news_curation: curator.can_access_news_curation
                            })}
                            disabled={processingId === curator.id}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span>Can post to network</span>
                        </label>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{curator.posts_created}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        curator.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {curator.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleRevokeAccess(curator.id, `${curator.first_name} ${curator.last_name}`)}
                        disabled={processingId === curator.id}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        {processingId === curator.id ? 'Processing...' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {curators.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No curators found. Grant access to alumni users to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Grant Access Modal */}
      {showGrantModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Grant Curator Access
            </h3>

            {/* Search Alumni */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Alumni (by name or email)
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Type to search..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Search Results */}
            {searchQuery && availableAlumni.length > 0 && !selectedAlumni && (
              <div className="mb-4 border border-gray-200 rounded-md max-h-60 overflow-y-auto">
                {availableAlumni.map((alumni) => (
                  <button
                    key={alumni.id}
                    onClick={() => {
                      setSelectedAlumni(alumni);
                      setSearchQuery('');
                      setAvailableAlumni([]);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    <div className="font-medium text-gray-900">
                      {alumni.first_name} {alumni.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{alumni.email}</div>
                  </button>
                ))}
              </div>
            )}

            {searchQuery && availableAlumni.length === 0 && (
              <p className="text-sm text-gray-500 mb-4">No matching alumni found</p>
            )}

            {/* Selected Alumni */}
            {selectedAlumni && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <div className="font-medium text-gray-900">
                  {selectedAlumni.first_name} {selectedAlumni.last_name}
                </div>
                <div className="text-sm text-gray-600">{selectedAlumni.email}</div>
              </div>
            )}

            {/* Permissions */}
            {selectedAlumni && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grant Permissions
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={permissions.can_access_news_curation}
                      onChange={(e) => setPermissions({
                        ...permissions,
                        can_access_news_curation: e.target.checked
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Can access news curation</strong> - View and curate Google News and YouTube content
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={permissions.can_post_to_network}
                      onChange={(e) => setPermissions({
                        ...permissions,
                        can_post_to_network: e.target.checked
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      <strong>Can post to network</strong> - Share curated content directly to alumni network (bypasses approval)
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={closeGrantModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGrantAccess}
                disabled={!selectedAlumni || processingId !== null}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingId !== null ? 'Processing...' : 'Grant Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminRouteGuard>
  );
};

export default AdminCuratorManagement;
